import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { Footer } from "@/components/Footer";

/**
 * Public, lightweight legal page that renders a markdown body fetched from
 * `/api/site/content/{slug}`. No auth required. Editable by admin.
 */
const ContentPage = ({ slug, fallbackTitle, testId }) => {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get(`/site/content/${slug}`)
      .then(({ data }) => mounted && setDoc(data))
      .catch((e) => mounted && setErr(e?.response?.data?.detail || "Failed to load"));
    return () => { mounted = false; };
  }, [slug]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid={testId}>
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-10 h-14 flex items-center gap-3">
          <Link to="/dashboard" className="text-muted-foreground hover:brand-text transition flex items-center gap-1.5 text-sm" data-testid="content-back">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            <span>{t("common.back")}</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-10 py-10">
        {err ? (
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 text-sm text-red-400" data-testid="content-error">{err}</div>
        ) : !doc ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <article data-testid="content-body">
            <Markdown source={doc.body} />
            <div className="mt-12 pt-4 border-t border-border text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
              Last updated · {new Date(doc.updated_at).toLocaleDateString()}
            </div>
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
};

export const ImpressumPage = () => <ContentPage slug="impressum" fallbackTitle="Impressum" testId="impressum-page" />;
export const PrivacyPage = () => <ContentPage slug="privacy" fallbackTitle="Privacy" testId="privacy-page" />;
